import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useUI } from '@/store/ui'
import { AuthAPI, type GateStatus } from '@/api/auth'
import { ApiError } from '@/api/client'
import { LandingAtmosphere } from '@/components/LandingAtmosphere'
import { DiwaSigil } from '@/components/DiwaSigil'
import { pendingInviteFromPath, postAuthDestination } from '@/lib/inviteIntent'
import {
  buildGateLoginUrl,
  callbackRedirectUri,
  loginMode,
  makeState,
  rememberState,
} from '@/lib/gateSso'
import './landing.css'

// The gate shares the landing's constellation sky. Talks directly to the diwa
// backend's /auth endpoints (bearer-token sessions), no Gate dependency; the
// token is stored in the UI store and attached by DiwaAPI on every request.

// Matches the server rule in products/diwa/src/diwa/auth/service.py — keep in sync.
const PASSWORD_MIN_LENGTH = 8

export function Login() {
  const { diwaUrl, setToken, pendingInvite, setPendingInvite } = useUI()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const from = (location.state as { from?: string } | null)?.from ?? null

  // A visitor who arrived from a pricing CTA carries ?plan=<tier>. We keep it
  // through registration so a would-be paying customer isn't silently dropped
  // on Keeper — and default them straight into the sign-up view.
  const selectedPlan = searchParams.get('plan') || searchParams.get('tier') || null
  // The Threshold's "continue with your code" link arrives as ?mode=signup&code=…
  // The code is REQUIRED (and spent) by /auth/signup while registration is
  // gated, so it must ride the signup payload; ignored once the doors are open.
  const accessCode = searchParams.get('code')
  const wantsSignup = searchParams.get('mode') === 'signup'

  // The moment the gate mounts, remember any invite the visitor was reaching for.
  // Persisting it (not just leaning on router state) means it survives even a
  // reload mid-registration, so the invite is still redeemed once they're in.
  useEffect(() => {
    const tok = from ? pendingInviteFromPath(from) : null
    if (tok) setPendingInvite(tok)
  }, [from, setPendingInvite])
  const [mode, setMode] = useState<'login' | 'signup'>(
    selectedPlan || wantsSignup ? 'signup' : 'login',
  )
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Forgot-password (blocked on email infra) — the backend returns a clear,
  // actionable message instead of a dead link; we surface it here.
  const [resetNote, setResetNote] = useState<string | null>(null)

  const api = useMemo(() => new AuthAPI(diwaUrl), [diwaUrl])

  // One-login (feature-flagged server-side via DIWA_GATE_SSO): when the backend
  // says the Gate flow is on, this page renders NOTHING but an immediate silent
  // redirect to the gate — no form, no button (the invisible standard, founder
  // ruling 2026-08-04). The local email/password form exists ONLY for flag-off.
  // `undefined` = probe in flight (hold quiet, never flash the form),
  // `null` = older/unreachable backend (form).
  const [gate, setGate] = useState<GateStatus | null | undefined>(undefined)
  useEffect(() => {
    let alive = true
    api
      .gateStatus()
      .then((g) => alive && setGate(g))
      .catch(() => alive && setGate(null))
    return () => {
      alive = false
    }
  }, [api])

  function signInWithDiwataId() {
    if (!gate?.enabled || !gate.gate_origin || !gate.client_id) return
    const state = makeState()
    rememberState(sessionStorage, state, from)
    window.location.assign(
      buildGateLoginUrl(
        gate.gate_origin,
        gate.client_id,
        callbackRedirectUri(window.location.origin),
        state,
      ),
    )
  }

  // The silent leg: the moment the gate reports the flow is on, go — exactly
  // once (the redirect unmounts us; the ref guards StrictMode double-effects).
  const gateMode = loginMode(gate)
  const bounced = useRef(false)
  useEffect(() => {
    if (gateMode === 'redirect' && !bounced.current) {
      bounced.current = true
      signInWithDiwataId()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateMode])

  const passwordLongEnough = password.length >= PASSWORD_MIN_LENGTH
  const passwordsMatch = confirm.length === 0 || password === confirm

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setResetNote(null)
    if (mode === 'signup') {
      if (!passwordLongEnough) {
        setError(`Password must be at least ${PASSWORD_MIN_LENGTH} characters.`)
        return
      }
      if (password !== confirm) {
        setError('Passwords do not match.')
        return
      }
    }
    setBusy(true)
    try {
      let checkoutPlan: string | null = null
      if (mode === 'signup') {
        const full = `${firstName.trim()} ${lastName.trim()}`.trim()
        const result = await api.signup(
          email,
          password,
          full || undefined,
          selectedPlan ?? undefined,
          accessCode ?? undefined,
        )
        // A paid tier only takes effect after checkout — route them there once in.
        if (result.checkout_required) checkoutPlan = result.plan
      }
      const { access_token } = await api.login(email, password)
      setToken(access_token)
      const invite = pendingInvite ?? (from ? pendingInviteFromPath(from) : null)
      if (checkoutPlan && !invite) {
        navigate(`/pricing?plan=${encodeURIComponent(checkoutPlan)}`, { replace: true })
      } else {
        navigate(postAuthDestination({ pendingInvite: invite, from }), { replace: true })
      }
    } catch (err) {
      // Surface the server's REAL reason (email taken vs weak password vs
      // invalid), not the old catch-all "email may already have access".
      const detail = err instanceof ApiError ? err.detail : undefined
      setError(
        detail ??
          (mode === 'signup'
            ? 'Something went wrong creating your account. Try again.'
            : 'Invalid email or password.'),
      )
    } finally {
      setBusy(false)
    }
  }

  async function requestReset() {
    setError(null)
    setResetNote(null)
    if (!email.trim()) {
      setError('Enter your email above first, then tap “Forgot password?”.')
      return
    }
    setBusy(true)
    try {
      const r = await api.forgotPassword(email.trim())
      setResetNote(r.message)
    } catch {
      setResetNote('Password reset is unavailable right now — email support@diwata.domains for help.')
    } finally {
      setBusy(false)
    }
  }

  // Gate flow on (or still resolving): the page is silent — atmosphere and a
  // spinner, never a form, never a button. The redirect effect above departs.
  if (gateMode !== 'form') {
    return (
      <div className="dwl">
        <LandingAtmosphere />
        <main className="dwl-login">
          <div className="dwl-login-card">
            <DiwaSigil size={60} className="dwl-login-sigil" />
            <h1 className="dwl-login-title">Diwa Domains</h1>
            <p className="dwl-login-sub" role="status">
              <span className="dwl-login-spinner" aria-hidden="true" />
              {' '}Taking you to sign in…
            </p>
          </div>
        </main>
      </div>
    )
  }

  const submitLabel = mode === 'login' ? 'Sign in' : 'Create account'
  const busyLabel = mode === 'login' ? 'Signing in…' : 'Creating account…'

  return (
    <div className="dwl">
      <LandingAtmosphere />
      <main className="dwl-login">
        <form className="dwl-login-card" onSubmit={submit}>
          <Link to="/" className="dwl-login-home" aria-label="Back to the Diwa Domains site">
            <DiwaSigil size={60} className="dwl-login-sigil" />
            <h1 className="dwl-login-title">Diwa Domains</h1>
          </Link>
          <p className="dwl-login-sub">
            {mode === 'login' ? 'Sign in to your domains' : 'Create your account: your world, as a constellation'}
          </p>

          <div className="dwl-login-form">
            {mode === 'signup' && (
              <div className="dwl-login-namerow">
                <input
                  className="dwl-login-field"
                  type="text"
                  aria-label="First name"
                  placeholder="First name"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
                <input
                  className="dwl-login-field"
                  type="text"
                  aria-label="Last name"
                  placeholder="Last name"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
            )}
            <input
              className="dwl-login-field"
              type="email"
              aria-label="Email"
              placeholder="Email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <input
              className="dwl-login-field"
              type="password"
              aria-label="Password"
              placeholder="Password"
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-describedby={mode === 'signup' ? 'dwl-pw-rules' : undefined}
              required
            />
            {mode === 'signup' && (
              <>
                <input
                  className="dwl-login-field"
                  type="password"
                  aria-label="Confirm password"
                  placeholder="Confirm password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
                <ul id="dwl-pw-rules" className="dwl-login-rules" aria-live="polite">
                  <li className={passwordLongEnough ? 'dwl-rule--ok' : ''}>
                    <span aria-hidden="true">{passwordLongEnough ? '✓' : '•'}</span> At least{' '}
                    {PASSWORD_MIN_LENGTH} characters
                  </li>
                  <li className={confirm.length > 0 && passwordsMatch ? 'dwl-rule--ok' : ''}>
                    <span aria-hidden="true">{confirm.length > 0 && passwordsMatch ? '✓' : '•'}</span>{' '}
                    Both passwords match
                  </li>
                </ul>
              </>
            )}
            {error && (
              <p className="dwl-login-error" role="alert">
                {error}
              </p>
            )}
            {resetNote && (
              <p className="dwl-login-note" role="status">
                {resetNote}
              </p>
            )}
            <button
              type="submit"
              className="dwl-btn dwl-login-submit"
              disabled={busy}
              aria-busy={busy}
            >
              {busy ? (
                <>
                  <span className="dwl-login-spinner" aria-hidden="true" />
                  {busyLabel}
                </>
              ) : (
                submitLabel
              )}
            </button>
            {mode === 'login' && (
              <button
                type="button"
                className="dwl-login-forgot"
                onClick={requestReset}
                disabled={busy}
              >
                Forgot password?
              </button>
            )}
          </div>

          {/* ONE story while the doors are closed: without an access code, "new
              here" leads to the waitlist, not a signup form the server would
              refuse. gate/status relays the flag; code holders keep the form. */}
          {mode === 'login' && gate?.registration_open !== true && !accessCode ? (
            <Link to="/waitlist" className="dwl-login-alt">
              New here? Join the waitlist
            </Link>
          ) : (
            <button
              type="button"
              className="dwl-login-alt"
              onClick={() => {
                setMode(mode === 'login' ? 'signup' : 'login')
                setError(null)
                setResetNote(null)
              }}
            >
              {mode === 'login' ? 'New here? Create an account' : 'Have an account? Sign in'}
            </button>
          )}
          <p style={{ margin: '14px 0 0', fontSize: 12, opacity: 0.65, textAlign: 'center' }}>
            By continuing you agree to the{' '}
            <Link to="/terms" style={{ color: 'inherit' }}>Terms</Link> and{' '}
            <Link to="/privacy" style={{ color: 'inherit' }}>Privacy Policy</Link>.
          </p>
          <a className="dwl-by-diwata" href="https://diwata.domains" target="_blank" rel="noreferrer">
            by Diwata ✦
          </a>
        </form>
      </main>
    </div>
  )
}
